import React, { useState, useEffect } from "react";
import {
  Container,
  Form,
  Button,
  Alert,
  Card,
  Dropdown,
  DropdownButton,
} from "react-bootstrap";
import { useLocation } from "react-router-dom";
import axios from "axios";
import config from "../../services/apiConfig";
import { apiPaths } from "../../services/apiPath";

const RescheduleChatScreen = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const hashcode = query.get("hashcode");

  const [reminderDatetime, setReminderDatetime] = useState("");
  const [reminderReason, setReminderReason] = useState("");
  const [status, setStatus] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        const response = await axios.post(config.BASE_URI + apiPaths.organiserData, {
          payload: {
            body: {
              query_type: "fetch_shared_outlet_operator_refference",
              hashcode: hashcode,
            },
          },
        });

        if (response.data.success) {
          setEventData(response.data.vendors);
        } else {
          setError(response.data.err || "Could not fetch event details.");
        }
      } catch (err) {
        setError("Something went wrong while fetching data.");
      }
    };

    if (hashcode) {
      fetchEventDetails();
    }
  }, [hashcode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(config.BASE_URI + apiPaths.organiserData, {
        payload: {
          body: {
            query_type: "reschedule_chat_reminder",
            hashcode,
            reminder_datetime: reminderDatetime,
            is_reminder_sent: false,
            reminder_reason: reminderReason,
          },
        },
      });

      if (response.data.success) {
        setStatus({ type: "success", message: "Reminder successfully scheduled." });
      } else {
        setStatus({ type: "error", message: "Failed to schedule reminder." });
      }
    } catch (err) {
      setStatus({ type: "error", message: "Error submitting reminder." });
      console.error(err);
    }
  };

  const formatCalendarTime = (dt) =>
    dt.toISOString().replace(/[-:]|\.000/g, "").slice(0, 15); // YYYYMMDDTHHmmss

  const getCalendarData = () => {
    if (!reminderDatetime || !eventData) return null;

    const startDate = new Date(reminderDatetime);
    const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // +30 mins

    const title = `Rescheduled Chat with ${eventData.vendor__title}`;
    const description = reminderReason || "Chat rescheduled";
    const location = `https://trucks.wherethetruckat.com/shared-outlet-operator?hashcode=${hashcode}`;

    return { startDate, endDate, title, description, location };
  };

  const openGoogleCalendar = () => {
    const data = getCalendarData();
    if (!data) return;

    const start = formatCalendarTime(data.startDate);
    const end = formatCalendarTime(data.endDate);

    const url = `https://calendar.google.com/calendar/u/0/r/eventedit?text=${encodeURIComponent(
      data.title
    )}&dates=${start}/${end}&details=${encodeURIComponent(
      data.description
    )}&location=${encodeURIComponent(data.location)}`;

    window.open(url, "_blank");
  };

  const downloadICSFile = () => {
    const data = getCalendarData();
    if (!data) return;

    const pad = (n) => String(n).padStart(2, "0");
    const formatICS = (d) =>
      `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(
        d.getUTCHours()
      )}${pad(d.getUTCMinutes())}00Z`;

    const start = formatICS(data.startDate);
    const end = formatICS(data.endDate);

    const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:${data.title}\nDESCRIPTION:${data.description}\nDTSTART:${start}\nDTEND:${end}\nLOCATION:${data.location}\nSTATUS:CONFIRMED\nEND:VEVENT\nEND:VCALENDAR`;

    const blob = new Blob([icsContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reschedule_chat.ics`;
    a.click();
  };

  return (
    <Container style={{ maxWidth: "700px", paddingTop: "40px" }}>
      <h3>Reschedule Chat</h3>

      {status && (
        <Alert variant={status.type === "success" ? "success" : "danger"}>
          {status.message}
        </Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}

      {eventData && (
        <Card className="mb-4">
          <Card.Header>
            <strong>Event & Vendor Details</strong>
          </Card.Header>
          <Card.Body>
            <p><strong>Event:</strong> {eventData.event_name || "N/A"}</p>
            <p><strong>Organiser:</strong> {eventData.event_organiser_full_name || "N/A"}</p>
            <p><strong>Vendor:</strong> {eventData.vendor__title || "N/A"}</p>
          </Card.Body>
        </Card>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Reschedule Datetime</Form.Label>
          <Form.Control
            type="datetime-local"
            value={reminderDatetime}
            onChange={(e) => setReminderDatetime(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Reschedule Reason</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reminderReason}
            onChange={(e) => setReminderReason(e.target.value)}
            placeholder="Explain why the chat is being rescheduled..."
            required
          />
        </Form.Group>

        <div className="d-flex gap-2">
          <Button type="submit" variant="primary">Submit Reminder</Button>
          <DropdownButton
            title="Add to Calendar"
            variant="primary"
            disabled={!reminderDatetime || !eventData}
          >
            <Dropdown.Item onClick={openGoogleCalendar}>Google Calendar</Dropdown.Item>
            <Dropdown.Item onClick={downloadICSFile}>Apple Calendar (.ics)</Dropdown.Item>
            <Dropdown.Item onClick={downloadICSFile}>Outlook Calendar (.ics)</Dropdown.Item>
          </DropdownButton>
        </div>
      </Form>
    </Container>
  );
};

export default RescheduleChatScreen;
